import React, { useEffect, useRef, useState } from "react";
import { IconButton, Tooltip } from "@mui/material";
import toast from "react-hot-toast";
import { ReactComponent as Close } from "../../assets/icons/close.svg";
import { ReactComponent as Info } from "../../assets/icons/info.svg";
import Modal from "../../components/Modal";
import Button from "../Button";
import Input from "../Input";
import getCurrency from "../../utils/getCurrency";
import { formatBrandName } from "../../utils/format";
import { getEffectivePrice } from "../../utils/priceUtils";

const CatalogDetailModal = ({ open, openSet, data }) => {
  const [showFullDescription, showFullDescriptionSet] = useState(false);
  const [selectedImage, selectedImageSet] = useState(data?.image_link);
  const [localQuantity, setLocalQuantity] = useState(data?.quantity || 1);
  const [localAdjustedPrice, setLocalAdjustedPrice] = useState(
    data?.adjustedPrice != null && data?.adjustedPrice !== '' ? String(data.adjustedPrice) : ''
  );
  const [savingAdjustedPrice, setSavingAdjustedPrice] = useState(false);
  const lastSyncedProductRef = useRef(null);

  useEffect(() => {
    if (!open || !data) {
      if (!open) lastSyncedProductRef.current = null;
      return;
    }
    const productKey = data?.id ?? data?.title ?? '';
    if (lastSyncedProductRef.current !== productKey) {
      lastSyncedProductRef.current = productKey;
      selectedImageSet(data?.image_link);
      setLocalQuantity(data?.quantity || 1);
      setLocalAdjustedPrice(
        data?.adjustedPrice != null && data?.adjustedPrice !== '' ? String(data.adjustedPrice) : ''
      );
    }
  }, [open, data?.id, data?.title, data?.image_link, data?.quantity, data?.adjustedPrice]);

  const handleQuantityChange = (newQuantity) => {
    const quantity = Math.max(1, newQuantity);
    setLocalQuantity(quantity);
    if (data?.onUpdateQuantity) {
      data.onUpdateQuantity(quantity);
    }
  };

  if (!data) return null;

  const hasAdjustedPrice = (localAdjustedPrice != null && localAdjustedPrice !== '') ||
    (data?.adjustedPrice != null && data?.adjustedPrice !== '');
  const displayPrice = getEffectivePrice({ ...data, adjustedPrice: localAdjustedPrice });
  const initialAdjustedPrice = data?.adjustedPrice != null && data?.adjustedPrice !== '' ? String(data.adjustedPrice).trim() : '';
  const adjustedPriceChanged = localAdjustedPrice.trim() !== initialAdjustedPrice;
  const sourceProvider = data.from || data.vendor;
  const normalizedSourceProvider = String(sourceProvider || '').toLowerCase();
  const isWayfairProduct = normalizedSourceProvider.includes('wayfair');
  const brandSource = isWayfairProduct ? sourceProvider : (data.sourceBrand || data.brand || data.vendor || data.from);
  const rawSubBrandName = isWayfairProduct ? (data.sourceBrand || data.brand || '') : '';
  const normalizedSubBrand = String(rawSubBrandName).trim().toLowerCase();
  const subBrandName = normalizedSubBrand && normalizedSubBrand !== normalizedSourceProvider ? rawSubBrandName : '';
  const formattedBrandName = formatBrandName(brandSource);
  let materialsImages = data.additionalImageLinks?.split(",")?.filter(image => image !== "");
  return (
    <Modal open={open} openSet={openSet} maxWidth="md">
      <div className="relative p-4 flex flex-col md:flex-row gap-10">
        <button
          onClick={() => openSet(false)}
          className="absolute top-0 right-0 w-8 h-8 text-white flex items-center justify-center transition-colors z-10"
        >
          <Close className="w-6 h-6 stroke-4" />
        </button>

        {/** IMAGE */}
        <div className="w-full md:w-[360px] h-[300px] md:h-[480px] rounded-md overflow-hidden">
          <img src={selectedImage} alt="catalog-image" className="size-full object-cover object-center" />
        </div>

        {/** NAME & DESCRIPTION & PRICE */}
        <div className="flex flex-col gap-3 flex-1">
          <div className="flex flex-col gap-0.5">
            <div className="text-lg font-semibold">{data.title}</div>
            <div className="text-primary/70">{data.productType}</div>
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-primary/60">{hasAdjustedPrice ? 'Adjusted price' : 'Price'}</span>
                  <div className="flex items-center gap-1.5">
                    <span className="text-lg font-semibold">{getCurrency(displayPrice)}</span>
                    {hasAdjustedPrice && (
                      <Tooltip title="This price was manually adjusted by designer">
                        <span
                          className="inline-flex items-center justify-center rounded-full bg-primary/10 text-primary p-0.5 cursor-help"
                          aria-label="Adjusted price info"
                        >
                          <Info className="w-4 h-4" />
                        </span>
                      </Tooltip>
                    )}
                  </div>
                  <span className="text-xs text-primary/50">Brand price: {getCurrency(Number(data.price) || 0)}</span>
                </div>
                {(data?.quantity || data?.onAddToRoom) && (
                  <div className="flex items-center gap-2 bg-gray-50 rounded-lg p-2">
                    <span className="text-sm text-gray-600">Qty:</span>
                    <IconButton 
                      size="small"
                      onClick={() => handleQuantityChange(localQuantity - 1)}
                      className="!w-6 !h-6 !bg-white !border !border-gray-300"
                    >
                      <span className="text-xs font-bold">-</span>
                    </IconButton>
                    <span className="text-sm font-semibold min-w-[20px] text-center">{localQuantity}</span>
                    <IconButton 
                      size="small"
                      onClick={() => handleQuantityChange(localQuantity + 1)}
                      className="!w-6 !h-6 !bg-white !border !border-gray-300"
                    >
                      <span className="text-xs font-bold">+</span>
                    </IconButton>
                  </div>
                )}
              </div>
              {data?.onUpdateAdjustedPrice && (
                <div className="flex flex-col gap-2">
                  <span className="text-xs text-primary/60">Adjusted price (optional)</span>
                  <div className="flex items-stretch gap-2 flex-wrap [&_.MuiInputBase-root]:!min-h-10">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Enter price"
                      value={localAdjustedPrice}
                      onChange={(e) => setLocalAdjustedPrice(e.target.value)}
                      containerClassName="max-w-[140px]"
                      size="small"
                    />
                    <Button
                      small
                      loading={savingAdjustedPrice}
                      disabled={savingAdjustedPrice || !adjustedPriceChanged}
                      className="!h-10 !min-h-10"
                      onClick={async () => {
                        const val = localAdjustedPrice.trim();
                        setSavingAdjustedPrice(true);
                        try {
                          await data.onUpdateAdjustedPrice(val === '' ? null : val);
                          toast.success("Adjusted price saved to catalog");
                        } catch (err) {
                          toast.error(err?.data?.message || "Failed to save adjusted price");
                        } finally {
                          setSavingAdjustedPrice(false);
                        }
                      }}
                    >
                      Save to catalog
                    </Button>
                  </div>
                </div>
              )}
            </div>
            {(data?.quantity || data?.onAddToRoom) && (
              <div className="text-sm text-gray-600">
                Total: {getCurrency(getEffectivePrice({ ...data, adjustedPrice: localAdjustedPrice }) * localQuantity)}
              </div>
            )}
          </div>
          <div className="text-primary/80 text-sm">{
            showFullDescription ?
              <>{data.description} <span className="text-primary underline cursor-pointer" onClick={() => showFullDescriptionSet(false)}>Read Less</span></> :
              data.description.length > 200 ?
                <>{data.description.substring(0, 200)} <span className="text-primary underline cursor-pointer" onClick={() => showFullDescriptionSet(true)}>Read More</span></> :
                data.description
          }</div>

          {/** MATERIALS */}
          <div className="flex flex-col gap-2">
            <div className="text-primary/60 font-semibold">Additional Images</div>
            <div className="flex flex-wrap gap-4">
              {materialsImages?.map((material, index) => {
                return (
                  <div key={index} className="size-14" onClick={() => selectedImageSet(material)}>
                    <img src={material} alt={`material-of-catalog-${index}`} className="size-full object-cover object-center" />
                  </div>
                );
              })}
            </div>
          </div>

          {/** Material */}
          {data?.material && (
            <div className="flex flex-col gap-2">
              <div className="text-primary/60 font-semibold">Material</div>
              <div className="flex flex-col gap-1 text-sm">
                {/** OVERALL */}
                <div>
                  <span>{data?.material}</span>
                </div>
              </div>
            </div>
          )}

          {/** DIMENSIONS */}
          <div className="flex flex-col gap-2">
            <div className="text-primary/60 font-semibold">Extras</div>
            <div className="flex flex-col gap-1 text-sm">
              {/** OVERALL */}
              <div>
                <span>•</span>
                <span> Size: </span>
                <span>{data?.size}</span>
              </div>
              {/** Inside SEAT */}
              <div>
                {/* <span>•</span>
                <span> Gender: </span>
                <span>{data?.gender}</span> */}
              </div>
              {brandSource && (
                <div>
                  <span>•</span>
                  <span> Brand: </span>
                  <span>{formattedBrandName}</span>
                </div>
              )}
              {subBrandName && (
                <div>
                  <span>•</span>
                  <span> Sub-brand: </span>
                  <span>{subBrandName}</span>
                </div>
              )}
              {/** seatHeight */}
              {/* <div>
                <span>•</span>
                <span> Seat Height: </span>
                <span>{data?.dimensions?.seatHeight}</span>
              </div> */}
              {/** diagonalDepth */}
              {/* <div>
                <span>•</span>
                <span> Diagonal Depth: </span>
                <span>{data?.dimensions?.diagonalDepth}</span>
              </div> */}
            </div>
          </div>
          
          {/** Add to Room Button */}
          {data?.onAddToRoom && (
            <div className="mt-4">
              <Button 
                onClick={() => {
                  data.onAddToRoom(localQuantity);
                  openSet(false);
                }}
                className="w-full"
              >
                Add to Room (Qty: {localQuantity})
              </Button>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default CatalogDetailModal;
